import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);
import { getConfluentAPI, streamKafkaLogs } from '../../utils/Api'
import axios from 'axios';


function MainDashboard() {
  const [confluentAPIResponse, setConfluentAPIResponse] = useState({});
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    const fetchConfluentData = async () => {
      try {
        const response = await axios.get(getConfluentAPI);
        setConfluentAPIResponse(response.data);
        console.log('Confluent API data updated:', new Date().toLocaleTimeString());
      } catch (error) {
        console.error('Error fetching Confluent API data:', error);
      }
    };
    
    // Initial fetch
    fetchConfluentData();
    
    // Set up interval to fetch every 10 seconds
    const intervalId = setInterval(fetchConfluentData, 10000);
    
    // Cleanup function to clear interval
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    let eventSource;
    let reconnectTimer;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    const reconnectDelay = 3000; // 3 seconds

    const connectEventSource = () => {
      try {
        eventSource = new EventSource(streamKafkaLogs);
        
        eventSource.onopen = () => {
          console.log('EventSource connection opened');
          reconnectAttempts = 0; // Reset attempts on successful connection
        };

        eventSource.onmessage = (event) => {
          try {
            const newLog = JSON.parse(event.data);
            setLogs((prevLogs) => [newLog, ...prevLogs.slice(0, 99)]); // Keep only latest 100 logs
          } catch (parseError) {
            console.error('Error parsing log data:', parseError);
          }
        };

        eventSource.onerror = (error) => {
          console.warn('EventSource connection error:', error);
          eventSource.close();
          
          // Attempt to reconnect if not exceeded max attempts
          if (reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            console.log(`Attempting to reconnect... (${reconnectAttempts}/${maxReconnectAttempts})`);
            
            reconnectTimer = setTimeout(() => {
              connectEventSource();
            }, reconnectDelay * reconnectAttempts); 
          } else {
            console.error('Max reconnection attempts reached. Please refresh the page.');
          }
        };

      } catch (error) {
        console.error('Error creating EventSource:', error);
      }
    };

    // Initial connection
    connectEventSource();

    // Cleanup function
    return () => {
      if (eventSource) {
        eventSource.close();
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
    };
  }, []);


  const metrics = [
    { label: 'Production', value: ((confluentAPIResponse?.brokerDetails?.bytesInPerSec)/1024).toFixed(6) + ' KB/s' || 'Loading...', color: '#00b4a0' },
    { label: 'Partition Replicas', value: confluentAPIResponse?.brokerDetails?.partitionReplicas || 'Loading...', color: '#1e90ff' },
    { label: 'Total Consumption', value: ((confluentAPIResponse?.brokerDetails?.bytesOutPerSec)/1024).toFixed(6) + ' KB/s' || 'Loading...', color: '#ab47bc' },
    { label: 'Status UP/ Down', value: confluentAPIResponse?.brokerDetails?.status || 'Loading...', color: '#ffa726' },
    { label: 'Total Topics', value: confluentAPIResponse?.topicStatus?.topicCount || 'Loading...', color: '#ff5252' },
    { label: 'Under Replicated Partitions', value: confluentAPIResponse?.topicStatus?.onlinePartitionCount || 'Loading...', color: '#26c6da' },
    { label: 'In sync Replicas', value: confluentAPIResponse?.topicStatus?.inSyncReplicas || 0, color: '#66bb6a' },
    { label: 'Out sync Replicas', value: confluentAPIResponse?.consumerStatus?.outOfSyncReplicas || 0, color: '#ef5350' },
  ];

  
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  });


  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a2240 0%, #1e3a5f 100%)',
      backgroundSize: 'cover',
      color: 'white',
      py: 6,
      px: { xs: 1, sm: 4 },
      position: 'relative'
    }}>
      {/* User name at top right */}
      {user && user.name && (
        <Typography sx={{ position: 'absolute', top: 24, right: 32, fontWeight: 700, fontSize: 18, color: '#e0f7fa', letterSpacing: 0.5 }}>
          Welcome, {user.name}
        </Typography>
      )}
      <Typography variant="h3" sx={{ mb: 3, fontWeight: 600, letterSpacing: -1, background: 'linear-gradient(90deg, #00b4a0 0%, #1e90ff 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        Confluent Dashboard
      </Typography>
      <Grid container spacing={3}>
        {/* Metrics */}
        {metrics.map((m, i) => (
          <Grid item xs={12} sm={6} md={3} key={m.label}>
            <Card sx={{ background: 'rgba(10,34,64,0.85)', borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ color: m.color, fontWeight: 700 }}>{m.label}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 1, color: 'white' }}>{m.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        
        {/* Live Logs */}
        <Grid item xs={12} md={12}>
          <Card sx={{ background: 'rgba(10,34,64,0.92)', borderRadius: 3, boxShadow: 3, minHeight: 320 }}>
            <CardContent sx={{ maxHeight: 600, overflowY: 'auto' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#60a5fa' }}>Live Logs</Typography>
              <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
              
              {/* Header Row */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                fontWeight: 700, 
                color: '#b0bec5', 
                mb: 2, 
                position: 'sticky', 
                top: 0, 
                background: 'rgba(10,34,64,0.92)', 
                zIndex: 2, 
                py: 1 
              }}>
                <Typography variant="caption" sx={{ minWidth: 80 }}>Timestamp</Typography>
                <Typography variant="caption" sx={{ minWidth: 60 }}>Host</Typography>
                <Typography variant="caption" sx={{ minWidth: 80 }}>Tag</Typography>
                <Typography variant="caption" sx={{ minWidth: 60 }}>Level</Typography>
                <Typography variant="caption" sx={{ flex: 1 }}>Message</Typography>
                <Typography variant="caption" sx={{ minWidth: 100 }}>Remote IP</Typography>
              </Box>

              {logs.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#b0bec5', textAlign: 'center', mt: 4 }}>
                  No logs received yet...
                </Typography>
              ) : (
                logs.slice(0, 50).map((log, i) => {
                  const getSeverityColor = (level) => {
                    switch(level) {
                      case 0: case 1: case 2: return '#ef4444'; // Emergency, Alert, Critical
                      case 3: return '#f97316'; // Error
                      case 4: return '#eab308'; // Warning
                      case 5: return '#3b82f6'; // Notice
                      case 6: return '#10b981'; // Info
                      case 7: return '#6b7280'; // Debug
                      default: return '#9ca3af';
                    }
                  };

                  const formatTimestamp = (timestamp) => {
                    if (!timestamp) return 'N/A';
                    const date = new Date(timestamp);
                    return date.toLocaleTimeString();
                  };

                  return (
                    <Box key={i} sx={{ 
                      mb: 1.5, 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2, 
                      p: 1, 
                      borderRadius: 1,
                      background: 'rgba(255,255,255,0.02)',
                      '&:hover': { background: 'rgba(255,255,255,0.05)' }
                    }}>
                      <Typography variant="caption" sx={{ color: '#b0bec5', minWidth: 80, fontSize: '0.75rem' }}>
                        {formatTimestamp(log.timestamp)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#60a5fa', minWidth: 60, fontSize: '0.85rem' }}>
                        {log.host || 'N/A'}
                      </Typography>
                      <Chip 
                        label={log.tag || 'system'} 
                        size="small" 
                        sx={{ 
                          minWidth: 80,
                          height: 24,
                          fontSize: '0.7rem',
                          background: '#ab47bc', 
                          color: 'white', 
                          fontWeight: 600 
                        }} 
                      />
                      <Chip 
                        label={`L${log.level}`} 
                        size="small" 
                        sx={{ 
                          minWidth: 60,
                          height: 24,
                          fontSize: '0.7rem',
                          background: getSeverityColor(log.level), 
                          color: 'white', 
                          fontWeight: 600 
                        }} 
                      />
                      <Typography variant="body2" sx={{ 
                        flex: 1, 
                        color: 'white', 
                        fontSize: '0.85rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {log.message || log.rawMessage || 'No message'}
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        color: '#ffa726', 
                        minWidth: 100, 
                        fontSize: '0.75rem',
                        textAlign: 'right'
                      }}>
                        {log.remoteAddress || 'N/A'}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default MainDashboard;
